"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-hot-toast";
import Head from "../Base/Head";
import Navbar from "./components/Navbar";
import ApiService from "../services/ApiService";

const EmployeeHeroCompact = () => (
  <section className="portal-hero portal-hero--compact portal-hero--employee-signup bg-9">
    <div className="portal-hero__overlay" />
    <div className="portal-hero__content">
      <h1 className="portal-hero__title">Employee Intake Form Non-profit Board Matchmaking</h1>
      <p className="portal-hero__subtitle">
        Share your background and preferences so we can match you with non-profit board opportunities.
      </p>
    </div>
  </section>
);

const CheckboxRow = ({ id, label, checked, disabled, onChange }) => (
  <label
    htmlFor={id}
    className={`nonprofit-signup__choice ${disabled ? "is-disabled" : ""}`}
  >
    <input
      id={id}
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="nonprofit-signup__choice-label">{label}</span>
  </label>
);

const RadioRow = ({ id, name, label, checked, onChange }) => (
  <label htmlFor={id} className="nonprofit-signup__choice">
    <input id={id} type="radio" name={name} checked={checked} onChange={onChange} />
    <span className="nonprofit-signup__choice-label">{label}</span>
  </label>
);

export default function EmployeeSignUp() {
  const formRef = useRef(null);
  const blurTimerRef = useRef(null);
  const pointerDownSectionRef = useRef(null);

  const user = useMemo(
    () => ({
      name: "Lauren Olivier",
      accountType: "EXECUTIVE",
      avatarUrl: "https://i.ibb.co/xqnrW3cK/woman-pfp.jpg",
      hasNotifications: true,
    }),
    []
  );

  const titleOptions = useMemo(
    () => [
      { value: "partner", label: "Partner" },
      { value: "md", label: "Managing Director" },
      { value: "principal", label: "Principal" },
    ],
    []
  );

  const issueAreaOptions = useMemo(
    () => [
      "Animal Welfare",
      "Arts & Culture",
      "Climate / Environment / Sustainability",
      'Economic Mobility ("Expanding Opportunity")',
      "Education",
      "Health & Wellness",
      "Human Services (hunger / housing)",
      "Youth Development",
    ],
    []
  );

  const professionalSkillsOptions = useMemo(
    () =>
      [
        "Finance / Accounting",
        "Investment Management",
        "Strategy",
        "Marketing / Communications",
        "Talent / Human Capital",
        "Legal / Compliance",
        "Risk management",
        "Operations",
        "Real Estate",
        "Technology / Engineering",
        "Data",
        "Fundraising",
      ].sort((a, b) => a.localeCompare(b)),
    []
  );

  const geographicOptions = useMemo(
    () => [
      { value: "nyc", label: "New York City" },
      { value: "greenwich", label: "Greenwich" },
      { value: "la", label: "Los Angeles / El Segundo" },
      { value: "national", label: "US national or multi-site", mutedHint: "(i.e., an organization with larger scale/reach)" },
      { value: "other", label: "Other (please indicate)" },
    ],
    []
  );

  const orgSizeOptions = useMemo(
    () => [
      { value: "small", label: "Small / early-stage (<$1m)" },
      { value: "mid", label: "Mid-sized ($1m - $10m)" },
      { value: "large", label: "Large / established ($10m+)" },
      { value: "no-pref", label: "No preference" },
    ],
    []
  );

  // Section 1
  const [name, setName] = useState("");
  const [apolloEmail, setApolloEmail] = useState("");
  const [business, setBusiness] = useState("");
  const [title, setTitle] = useState("");
  const [officeLocation, setOfficeLocation] = useState("");
  const [disclosureAccepted, setDisclosureAccepted] = useState(false);

  // Section 2
  const [boardExperience, setBoardExperience] = useState("");
  const [boardExperienceDesc, setBoardExperienceDesc] = useState("");
  const [volunteered, setVolunteered] = useState("");
  const [volunteeredDesc, setVolunteeredDesc] = useState("");

  // Section 3
  const [issueAreas, setIssueAreas] = useState([]);
  const [issueAreaOther, setIssueAreaOther] = useState("");

  // Section 4
  const [professionalSkills, setProfessionalSkills] = useState([]);
  const [professionalSkillsOther, setProfessionalSkillsOther] = useState("");

  // Section 5
  const [timeCommit, setTimeCommit] = useState("");
  const [giveGet, setGiveGet] = useState("");
  const [giveGetExpanded, setGiveGetExpanded] = useState(false);
  const [meetingFormat, setMeetingFormat] = useState("");

  // Section 6
  const [geoPreference, setGeoPreference] = useState("");
  const [geoOther, setGeoOther] = useState("");
  const [orgSize, setOrgSize] = useState("");
  const [openToEarlyStage, setOpenToEarlyStage] = useState("");

  // Section 7
  const [roleInterest, setRoleInterest] = useState("");
  const [howSoon, setHowSoon] = useState("");

  // Section 8
  const [additionalContext, setAdditionalContext] = useState("");

  // Section 9
  const [conflictsAware, setConflictsAware] = useState("");
  const [conflictsExplain, setConflictsExplain] = useState("");
  const [policiesAgree, setPoliciesAgree] = useState("");

  // Section 10
  const [ackNoGuarantee, setAckNoGuarantee] = useState("");
  const [permissionShare, setPermissionShare] = useState("");

  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const emailLooksValid = (val) => {
    const v = `${val}`.trim();
    if (!v) return false;
    return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(v);
  };

  const section1Valid =
    name.trim().length > 0 &&
    emailLooksValid(apolloEmail) &&
    disclosureAccepted;

  const formValid =
    section1Valid &&
    boardExperience.length > 0 &&
    issueAreas.length > 0 &&
    professionalSkills.length > 0 &&
    timeCommit.length > 0 &&
    giveGet.length > 0 &&
    meetingFormat.length > 0 &&
    geoPreference.length > 0 &&
    (geoPreference !== "other" || geoOther.trim().length > 0) &&
    orgSize.length > 0 &&
    openToEarlyStage.length > 0 &&
    roleInterest.length > 0 &&
    howSoon.length > 0 &&
    conflictsAware.length > 0 &&
    (conflictsAware !== "yes" || conflictsExplain.trim().length > 0) &&
    policiesAgree === "yes" &&
    ackNoGuarantee === "yes" &&
    permissionShare.length > 0;

  const head = {
    title: "Apollo Purpose Portal | Employee Intake",
    description: "Apollo Purpose Portal: Employee intake for non-profit board matchmaking.",
    url: `https://apollopurposeportal.com/employee-signup`,
    image: "",
  };

  const setIssueAreaChecked = (area, checked) => {
    setIssueAreas((prev) =>
      checked ? [...new Set([...prev, area])] : prev.filter((a) => a !== area)
    );
  };

  const setProfessionalSkillChecked = (skill, checked) => {
    setProfessionalSkills((prev) =>
      checked ? [...new Set([...prev, skill])] : prev.filter((s) => s !== skill)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitAttempted(true);
    if (!formValid) return;
    setSubmitted(false);

    const form = {
      name,
      apolloEmail,
      business,
      title,
      officeLocation,
      disclosureAccepted,
      boardExperience,
      boardExperienceDesc,
      volunteered,
      volunteeredDesc,
      issueAreas,
      issueAreaOther,
      professionalSkills,
      professionalSkillsOther,
      timeCommit,
      giveGet,
      meetingFormat,
      geoPreference,
      geoOther,
      orgSize,
      openToEarlyStage,
      roleInterest,
      howSoon,
      additionalContext,
      conflictsAware,
      conflictsExplain,
      policiesAgree,
      ackNoGuarantee,
      permissionShare,
    };

    try {
      setIsSubmitting(true);
      const api = new ApiService();
      const response = await api.post("landmark/apollo/submitEmployeeIntake", {
        form,
      });
      if (!response || response.error || !response.success) {
        setSubmitted(false);
        toast.error(response?.error || "Could not submit form. Please try again.");
        return;
      }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      setSubmitted(false);
      toast.error("Could not submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateActiveSectionClass = (activeSection) => {
    const formEl = formRef.current;
    if (!formEl) return;

    formEl
      .querySelectorAll(".nonprofit-signup__section.is-focus-active")
      .forEach((sectionEl) => {
        if (sectionEl !== activeSection) sectionEl.classList.remove("is-focus-active");
      });

    if (activeSection) activeSection.classList.add("is-focus-active");
  };

  const handleFormFocusCapture = (e) => {
    const sectionEl = e.target.closest?.(".nonprofit-signup__section");
    if (!sectionEl) return;

    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }

    updateActiveSectionClass(sectionEl);
  };

  const handleFormMouseDownCapture = (e) => {
    const sectionEl = e.target.closest?.(".nonprofit-signup__section");
    if (!sectionEl) return;
    pointerDownSectionRef.current = sectionEl;
    updateActiveSectionClass(sectionEl);
  };

  const handleFormBlurCapture = (e) => {
    const sourceSection = e.target.closest?.(".nonprofit-signup__section");
    const nextSection = e.relatedTarget?.closest?.(".nonprofit-signup__section") || null;

    // Native focus handoff inside the same section should never clear highlight.
    if (sourceSection && nextSection && sourceSection === nextSection) {
      updateActiveSectionClass(sourceSection);
      return;
    }

    if (blurTimerRef.current) {
      window.clearTimeout(blurTimerRef.current);
    }

    blurTimerRef.current = window.setTimeout(() => {
      const activeElement = document.activeElement;
      const activeSection = activeElement?.closest?.(".nonprofit-signup__section") || null;
      if (activeSection) {
        updateActiveSectionClass(activeSection);
      } else if (pointerDownSectionRef.current) {
        // Preserve highlight when user clicks non-focusable content within a section.
        updateActiveSectionClass(pointerDownSectionRef.current);
      } else {
        updateActiveSectionClass(null);
      }
      pointerDownSectionRef.current = null;
      blurTimerRef.current = null;
    }, 120);
  };

  useEffect(() => {
    return () => {
      if (blurTimerRef.current) {
        window.clearTimeout(blurTimerRef.current);
      }
    };
  }, []);

  const pageStaggerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: 0.08,
        delayChildren: 0.03,
      },
    },
  };

  const fadeUpVariants = {
    hidden: { opacity: 0, y: 16 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.34, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <>
      <Head head={head} noTwitterCard={true} />
      <div className="ed-wrapper">
        <div className="ed-main">
          <Navbar user={user} currentPage="portal" />

          <motion.div className="portal-page" initial="hidden" animate="show" variants={pageStaggerVariants}>
            <motion.div variants={fadeUpVariants}>
              <EmployeeHeroCompact />
            </motion.div>

            <motion.div
              className="nonprofit-signup__wrap nonprofit-signup__wrap--employee"
              variants={fadeUpVariants}
            >
              <header className="nonprofit-signup__page-header">
                <h2 className="nonprofit-signup__heading">Non-profit board matchmaking</h2>
                <p className="nonprofit-signup__intro">
                  Complete the form below.
                </p>
              </header>

              {submitted ? (
                <div className="nonprofit-signup__notice" role="status">
                  <div className="nonprofit-signup__notice-title">
                    CONGRATULATIONS! Your profile is completed.
                  </div>
                  <div className="nonprofit-signup__notice-text">
                    Thank you for submitting information to create your Apollo Purpose
                    Portal profile. We appreciate your commitment to Lead Responsibly,
                    and will be in touch when there is a match. In the meantime, please
                    peruse the existing opportunities on the portal{" "}
                    <a className="nonprofit-signup__notice-link" href="/portal">here</a>, and please email{" "}
                    <a
                      className="nonprofit-signup__notice-link"
                      href="mailto:Citizenship@apollo.com?subject=Apollo%20Purpose%20Portal%20Inquiry"
                    >
                      Citizenship@apollo.com
                    </a>{" "}
                    with any questions or feedback.
                  </div>
                </div>
              ) : null}

              <form
                ref={formRef}
                className="nonprofit-signup__card"
                onSubmit={handleSubmit}
                onFocusCapture={handleFormFocusCapture}
                onBlurCapture={handleFormBlurCapture}
                onMouseDownCapture={handleFormMouseDownCapture}
                onTouchStartCapture={handleFormMouseDownCapture}
              >
                {/* 1. Basics & Eligibility */}
                <section className="nonprofit-signup__section">
                  <div className="nonprofit-signup__section-top">
                    <div className="nonprofit-signup__step">
                      <span className="nonprofit-signup__step-number">1</span>
                    </div>
                    <div>
                      <div className="nonprofit-signup__kicker">Basics & Eligibility</div>
                      <div className="nonprofit-signup__subkicker">
                        Confirm your details and disclosure to unlock the rest of the form.
                      </div>
                    </div>
                  </div>

                  <div className="nonprofit-signup__grid">
                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">
                        Name <span className="nonprofit-signup__req">*</span>
                      </div>
                      <input
                        className="nonprofit-signup__input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Full name"
                      />
                      {submitAttempted && !name.trim().length ? (
                        <div className="nonprofit-signup__error">Please enter your name.</div>
                      ) : null}
                    </div>
                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">
                        Apollo email address <span className="nonprofit-signup__req">*</span>
                      </div>
                      <input
                        className="nonprofit-signup__input"
                        type="email"
                        value={apolloEmail}
                        onChange={(e) => setApolloEmail(e.target.value)}
                        placeholder="name@apollo.com"
                      />
                      {submitAttempted && !emailLooksValid(apolloEmail) ? (
                        <div className="nonprofit-signup__error">
                          Please enter a valid Apollo email address.
                        </div>
                      ) : null}
                    </div>
                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">Business</div>
                      <input
                        className="nonprofit-signup__input"
                        value={business}
                        onChange={(e) => setBusiness(e.target.value)}
                        placeholder="e.g., Credit or Hybrid or Human Capital, etc"
                      />
                    </div>
                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">Title</div>
                      <select
                        className="nonprofit-signup__input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      >
                        <option value="">Select title</option>
                        {titleOptions.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="nonprofit-signup__field">
                      <div className="nonprofit-signup__label">Office Location</div>
                      <input
                        className="nonprofit-signup__input"
                        value={officeLocation}
                        onChange={(e) => setOfficeLocation(e.target.value)}
                        placeholder="e.g., New York, Greenwich"
                      />
                    </div>
                  </div>

                  <div className="nonprofit-signup__field" style={{ marginTop: "1rem" }}>
                    <CheckboxRow
                      id="disclosure"
                      label="I confirm I am interested in serving on a non-profit board in my individual capacity (and not as a representative of Apollo)."
                      checked={disclosureAccepted}
                      disabled={false}
                      onChange={setDisclosureAccepted}
                    />
                    {submitAttempted && !disclosureAccepted ? (
                      <div className="nonprofit-signup__error">
                        You must confirm the disclosure above to continue.
                      </div>
                    ) : null}
                  </div>
                </section>

                {disclosureAccepted && (
                  <>
                    {/* 2. Experience Level */}
                    <section className="nonprofit-signup__section">
                      <div className="nonprofit-signup__section-top">
                        <div className="nonprofit-signup__step">
                          <span className="nonprofit-signup__step-number">2</span>
                        </div>
                        <div>
                          <div className="nonprofit-signup__kicker">Experience Level</div>
                        </div>
                      </div>

                      <div className="nonprofit-signup__label">
                        Have you previously served on a non-profit board?{" "}
                        <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="board-yes-current"
                          name="boardExperience"
                          label="Yes – I am currently on a non-profit board"
                          checked={boardExperience === "yes-current"}
                          onChange={() => setBoardExperience("yes-current")}
                        />
                        <RadioRow
                          id="board-yes-past"
                          name="boardExperience"
                          label="Yes – I have served in the past but am not currently on a non-profit board"
                          checked={boardExperience === "yes-past"}
                          onChange={() => setBoardExperience("yes-past")}
                        />
                        <RadioRow
                          id="board-no"
                          name="boardExperience"
                          label="No, first-time board member"
                          checked={boardExperience === "no"}
                          onChange={() => setBoardExperience("no")}
                        />
                      </div>

                      {(boardExperience === "yes-current" || boardExperience === "yes-past") && (
                        <div className="nonprofit-signup__field" style={{ marginTop: "1rem" }}>
                          <div className="nonprofit-signup__label">
                            Brief description of current or prior board experience (optional)
                          </div>
                          <textarea
                            className="nonprofit-signup__textarea"
                            rows={3}
                            value={boardExperienceDesc}
                            onChange={(e) => setBoardExperienceDesc(e.target.value)}
                            placeholder="Describe your board experience"
                          />
                        </div>
                      )}

                      <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                        Have you previously volunteered with any organizations or attended
                        events? (optional)
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="vol-yes"
                          name="volunteered"
                          label="Yes"
                          checked={volunteered === "yes"}
                          onChange={() => setVolunteered("yes")}
                        />
                        <RadioRow
                          id="vol-no"
                          name="volunteered"
                          label="No"
                          checked={volunteered === "no"}
                          onChange={() => setVolunteered("no")}
                        />
                      </div>
                      {volunteered === "yes" && (
                        <div className="nonprofit-signup__field" style={{ marginTop: "0.5rem" }}>
                          <div className="nonprofit-signup__label">Please describe:</div>
                          <textarea
                            className="nonprofit-signup__textarea"
                            rows={2}
                            value={volunteeredDesc}
                            onChange={(e) => setVolunteeredDesc(e.target.value)}
                            placeholder="Describe your volunteering or events"
                          />
                        </div>
                      )}
                    </section>

                    {/* 3. Interests */}
                    <section className="nonprofit-signup__section">
                      <div className="nonprofit-signup__section-top">
                        <div className="nonprofit-signup__step">
                          <span className="nonprofit-signup__step-number">3</span>
                        </div>
                        <div>
                          <div className="nonprofit-signup__kicker">Interests</div>
                          <div className="nonprofit-signup__subkicker">Select all that apply.</div>
                        </div>
                      </div>

                      <div className="nonprofit-signup__label">
                        Primary issue area(s) of interest <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-grid">
                        {issueAreaOptions.map((area) => {
                          const id = `issue-${area.replace(/[^\w]+/g, "-").toLowerCase()}`;
                          return (
                            <CheckboxRow
                              key={area}
                              id={id}
                              label={area}
                              checked={issueAreas.includes(area)}
                              disabled={false}
                              onChange={(checked) => setIssueAreaChecked(area, checked)}
                            />
                          );
                        })}
                        <CheckboxRow
                          id="issue-other"
                          label="Other (specify)"
                          checked={issueAreas.includes("Other")}
                          disabled={false}
                          onChange={(checked) => setIssueAreaChecked("Other", checked)}
                        />
                      </div>
                      {issueAreas.includes("Other") && (
                        <div className="nonprofit-signup__field" style={{ marginTop: "0.75rem" }}>
                          <input
                            className="nonprofit-signup__input"
                            value={issueAreaOther}
                            onChange={(e) => setIssueAreaOther(e.target.value)}
                            placeholder="Please specify"
                          />
                        </div>
                      )}
                      {submitAttempted && issueAreas.length === 0 ? (
                        <div className="nonprofit-signup__error">
                          Please select at least one issue area.
                        </div>
                      ) : null}
                    </section>

                    {/* 4. Skills You Can Offer */}
                    <section className="nonprofit-signup__section">
                      <div className="nonprofit-signup__section-top">
                        <div className="nonprofit-signup__step">
                          <span className="nonprofit-signup__step-number">4</span>
                        </div>
                        <div>
                          <div className="nonprofit-signup__kicker">Skills You Can Offer</div>
                          <div className="nonprofit-signup__subkicker">
                            Professional skills you would bring to a board (select all that apply).
                          </div>
                        </div>
                      </div>

                      <div className="nonprofit-signup__choices-grid">
                        {professionalSkillsOptions.map((skill) => {
                          const id = `skill-${skill.replace(/[^\w]+/g, "-").toLowerCase()}`;
                          return (
                            <CheckboxRow
                              key={skill}
                              id={id}
                              label={skill}
                              checked={professionalSkills.includes(skill)}
                              disabled={false}
                              onChange={(checked) => setProfessionalSkillChecked(skill, checked)}
                            />
                          );
                        })}
                        <CheckboxRow
                          id="skill-other"
                          label="Other (specify)"
                          checked={professionalSkills.includes("Other")}
                          disabled={false}
                          onChange={(checked) => setProfessionalSkillChecked("Other", checked)}
                        />
                      </div>
                      {professionalSkills.includes("Other") && (
                        <div className="nonprofit-signup__field" style={{ marginTop: "0.75rem" }}>
                          <input
                            className="nonprofit-signup__input"
                            value={professionalSkillsOther}
                            onChange={(e) => setProfessionalSkillsOther(e.target.value)}
                            placeholder="Please specify"
                          />
                        </div>
                      )}
                      {submitAttempted && professionalSkills.length === 0 ? (
                        <div className="nonprofit-signup__error">
                          Please select at least one skill area.
                        </div>
                      ) : null}
                    </section>

                    {/* 5. Capacity & Constraints */}
                    <section className="nonprofit-signup__section">
                      <div className="nonprofit-signup__section-top">
                        <div className="nonprofit-signup__step">
                          <span className="nonprofit-signup__step-number">5</span>
                        </div>
                        <div>
                          <div className="nonprofit-signup__kicker">Capacity & Constraints</div>
                        </div>
                      </div>

                      <div className="nonprofit-signup__label">
                        Estimated time you can commit annually{" "}
                        <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="time-light"
                          name="timeCommit"
                          label="Light (quarterly meetings only)"
                          checked={timeCommit === "light"}
                          onChange={() => setTimeCommit("light")}
                        />
                        <RadioRow
                          id="time-moderate"
                          name="timeCommit"
                          label="Moderate (quarterly board + committee meetings)"
                          checked={timeCommit === "moderate"}
                          onChange={() => setTimeCommit("moderate")}
                        />
                        <RadioRow
                          id="time-heavy"
                          name="timeCommit"
                          label="Heavy (working board / active involvement)"
                          checked={timeCommit === "heavy"}
                          onChange={() => setTimeCommit("heavy")}
                        />
                      </div>

                      <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                        Many non-profits expect their Board of Directors to commit to a
                        &quot;give / get&quot; fundraising expectation. Please indicate where
                        you are comfortable.{" "}
                        <span className="nonprofit-signup__req">*</span>{" "}
                        <button
                          type="button"
                          className="nonprofit-signup__give-get-trigger"
                          onClick={() => setGiveGetExpanded((prev) => !prev)}
                          aria-expanded={giveGetExpanded}
                          aria-controls="give-get-definition"
                          id="give-get-trigger"
                        >
                          What is give/get?
                        </button>
                      </div>
                      <AnimatePresence initial={false}>
                        {giveGetExpanded && (
                          <motion.div
                            id="give-get-definition"
                            className="nonprofit-signup__unlock-notice nonprofit-signup__give-get-notice"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                              height: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
                              opacity: { duration: 0.25 },
                            }}
                            style={{ overflow: "hidden", marginTop: "0.5rem" }}
                          >
                            <p className="nonprofit-signup__unlock-text nonprofit-signup__give-get-text">
                              A give/get policy is typically an annual, board-mandated
                              requirement for directors to either personally donate a
                              specific amount (&quot;give&quot;) or procure donations from
                              their network (&quot;get&quot;). It ensures accountability,
                              provides financial stability, and establishes clear
                              fundraising expectations for leadership.
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      <div className="nonprofit-signup__choices-stack" style={{ marginTop: "0.75rem" }}>
                        <RadioRow
                          id="give-10"
                          name="giveGet"
                          label="Up to $10,000"
                          checked={giveGet === "10"}
                          onChange={() => setGiveGet("10")}
                        />
                        <RadioRow
                          id="give-10-25"
                          name="giveGet"
                          label="$10,000 - $25,000"
                          checked={giveGet === "10-25"}
                          onChange={() => setGiveGet("10-25")}
                        />
                        <RadioRow
                          id="give-25-50"
                          name="giveGet"
                          label="$25,000 - $50,000"
                          checked={giveGet === "25-50"}
                          onChange={() => setGiveGet("25-50")}
                        />
                        <RadioRow
                          id="give-50-100"
                          name="giveGet"
                          label="$50,000 - $100,000"
                          checked={giveGet === "50-100"}
                          onChange={() => setGiveGet("50-100")}
                        />
                        <RadioRow
                          id="give-no"
                          name="giveGet"
                          label="Prefer not to disclose"
                          checked={giveGet === "no-disclose"}
                          onChange={() => setGiveGet("no-disclose")}
                        />
                      </div>

                      <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                        Preferred board meeting format <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="fmt-virtual"
                          name="meetingFormat"
                          label="Virtual"
                          checked={meetingFormat === "virtual"}
                          onChange={() => setMeetingFormat("virtual")}
                        />
                        <RadioRow
                          id="fmt-inperson"
                          name="meetingFormat"
                          label="In-person"
                          checked={meetingFormat === "in-person"}
                          onChange={() => setMeetingFormat("in-person")}
                        />
                        <RadioRow
                          id="fmt-hybrid"
                          name="meetingFormat"
                          label="Hybrid"
                          checked={meetingFormat === "hybrid"}
                          onChange={() => setMeetingFormat("hybrid")}
                        />
                      </div>

                      {submitAttempted &&
                        (!timeCommit || !giveGet || !meetingFormat) && (
                          <div className="nonprofit-signup__error">
                            Please complete all capacity & constraints fields.
                          </div>
                        )}
                    </section>

                    {/* 6. Logistics */}
                    <section className="nonprofit-signup__section">
                      <div className="nonprofit-signup__section-top">
                        <div className="nonprofit-signup__step">
                          <span className="nonprofit-signup__step-number">6</span>
                        </div>
                        <div>
                          <div className="nonprofit-signup__kicker">Logistics</div>
                        </div>
                      </div>

                      <div className="nonprofit-signup__label">
                        Geographic preference <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        {geographicOptions.map((opt) => (
                          <RadioRow
                            key={opt.value}
                            id={`geo-${opt.value}`}
                            name="geoPreference"
                            label={
                              <>
                                {opt.label}
                                {opt.mutedHint ? (
                                  <span className="nonprofit-signup__choice-label-muted">
                                    {" "}
                                    {opt.mutedHint}
                                  </span>
                                ) : null}
                              </>
                            }
                            checked={geoPreference === opt.value}
                            onChange={() => setGeoPreference(opt.value)}
                          />
                        ))}
                      </div>
                      {geoPreference === "other" && (
                        <div className="nonprofit-signup__field" style={{ marginTop: "0.5rem" }}>
                          <input
                            className="nonprofit-signup__input"
                            value={geoOther}
                            onChange={(e) => setGeoOther(e.target.value)}
                            placeholder="e.g., your hometown or local area of interest"
                          />
                          {submitAttempted && geoPreference === "other" && !geoOther.trim().length ? (
                            <div className="nonprofit-signup__error">
                              Please indicate your geographic preference.
                            </div>
                          ) : null}
                        </div>
                      )}

                      <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                        Preferred organization size <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        {orgSizeOptions.map((opt) => (
                          <RadioRow
                            key={opt.value}
                            id={`size-${opt.value}`}
                            name="orgSize"
                            label={opt.label}
                            checked={orgSize === opt.value}
                            onChange={() => setOrgSize(opt.value)}
                          />
                        ))}
                      </div>

                      <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                        Are you open to earlier stage, start-up type organizations?{" "}
                        <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="early-yes"
                          name="openToEarlyStage"
                          label="Yes"
                          checked={openToEarlyStage === "yes"}
                          onChange={() => setOpenToEarlyStage("yes")}
                        />
                        <RadioRow
                          id="early-no"
                          name="openToEarlyStage"
                          label="No"
                          checked={openToEarlyStage === "no"}
                          onChange={() => setOpenToEarlyStage("no")}
                        />
                        <RadioRow
                          id="early-unsure"
                          name="openToEarlyStage"
                          label="Unsure / Haven’t thought about it"
                          checked={openToEarlyStage === "unsure"}
                          onChange={() => setOpenToEarlyStage("unsure")}
                        />
                      </div>

                      {submitAttempted &&
                        (!geoPreference || (geoPreference === "other" && !geoOther.trim()) || !orgSize || !openToEarlyStage) && (
                          <div className="nonprofit-signup__error">
                            Please complete all logistics fields.
                          </div>
                        )}
                    </section>

                    {/* 7. Matching Preferences */}
                    <section className="nonprofit-signup__section">
                      <div className="nonprofit-signup__section-top">
                        <div className="nonprofit-signup__step">
                          <span className="nonprofit-signup__step-number">7</span>
                        </div>
                        <div>
                          <div className="nonprofit-signup__kicker">Matching Preferences</div>
                        </div>
                      </div>

                      <div className="nonprofit-signup__label">
                        What type of role most interests you?{" "}
                        <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="role-governing"
                          name="roleInterest"
                          label="Governing Board or Board of Trustees"
                          checked={roleInterest === "governing"}
                          onChange={() => setRoleInterest("governing")}
                        />
                        <RadioRow
                          id="role-finance"
                          name="roleInterest"
                          label="Finance / Investment Committee"
                          checked={roleInterest === "finance"}
                          onChange={() => setRoleInterest("finance")}
                        />
                        <RadioRow
                          id="role-advisory"
                          name="roleInterest"
                          label="Advisory or Committee (non-voting)"
                          checked={roleInterest === "advisory"}
                          onChange={() => setRoleInterest("advisory")}
                        />
                        <RadioRow
                          id="role-any"
                          name="roleInterest"
                          label="Open to any"
                          checked={roleInterest === "any"}
                          onChange={() => setRoleInterest("any")}
                        />
                      </div>

                      <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                        How soon are you looking to join a board?{" "}
                        <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="soon-now"
                          name="howSoon"
                          label="Immediately"
                          checked={howSoon === "immediately"}
                          onChange={() => setHowSoon("immediately")}
                        />
                        <RadioRow
                          id="soon-3-6"
                          name="howSoon"
                          label="Next 3–6 months"
                          checked={howSoon === "3-6"}
                          onChange={() => setHowSoon("3-6")}
                        />
                        <RadioRow
                          id="soon-exploring"
                          name="howSoon"
                          label="Just exploring"
                          checked={howSoon === "exploring"}
                          onChange={() => setHowSoon("exploring")}
                        />
                      </div>

                      {submitAttempted && (!roleInterest || !howSoon) ? (
                        <div className="nonprofit-signup__error">
                          Please complete matching preferences.
                        </div>
                      ) : null}
                    </section>

                    {/* 8. Additional context (optional) */}
                    <section className="nonprofit-signup__section">
                      <div className="nonprofit-signup__section-top">
                        <div className="nonprofit-signup__step">
                          <span className="nonprofit-signup__step-number">8</span>
                        </div>
                        <div>
                          <div className="nonprofit-signup__kicker">Additional Context (optional)</div>
                        </div>
                      </div>

                      <div className="nonprofit-signup__label">
                        Please tell us anything else about what you’re looking for, or
                        personal experiences and/or passions that will help us find you
                        the best fit.
                      </div>
                      <textarea
                        className="nonprofit-signup__textarea"
                        rows={4}
                        value={additionalContext}
                        onChange={(e) => setAdditionalContext(e.target.value)}
                        placeholder="Optional"
                      />
                    </section>

                    {/* 9. Conflicts & Compliance */}
                    <section className="nonprofit-signup__section">
                      <div className="nonprofit-signup__section-top">
                        <div className="nonprofit-signup__step">
                          <span className="nonprofit-signup__step-number">9</span>
                        </div>
                        <div>
                          <div className="nonprofit-signup__kicker">Conflicts & Compliance</div>
                        </div>
                      </div>

                      <div className="nonprofit-signup__label">
                        Are you aware of any potential conflicts between your board service
                        interests and Apollo, its funds, or portfolio companies? (Y/N — explain
                        if yes) <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="conf-yes"
                          name="conflictsAware"
                          label="Yes"
                          checked={conflictsAware === "yes"}
                          onChange={() => setConflictsAware("yes")}
                        />
                        <RadioRow
                          id="conf-no"
                          name="conflictsAware"
                          label="No"
                          checked={conflictsAware === "no"}
                          onChange={() => setConflictsAware("no")}
                        />
                      </div>
                      {conflictsAware === "yes" && (
                        <div className="nonprofit-signup__field" style={{ marginTop: "0.5rem" }}>
                          <div className="nonprofit-signup__label">Please explain:</div>
                          <textarea
                            className="nonprofit-signup__textarea"
                            rows={3}
                            value={conflictsExplain}
                            onChange={(e) => setConflictsExplain(e.target.value)}
                            placeholder="Describe any potential conflicts"
                          />
                          {submitAttempted && conflictsAware === "yes" && !conflictsExplain.trim().length ? (
                            <div className="nonprofit-signup__error">
                              Please explain potential conflicts.
                            </div>
                          ) : null}
                        </div>
                      )}

                      <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                        Have you reviewed and do you agree to comply with Apollo’s outside
                        activities / conflicts policies as they relate to board service?
                        (Y/N) <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="pol-yes"
                          name="policiesAgree"
                          label="Yes"
                          checked={policiesAgree === "yes"}
                          onChange={() => setPoliciesAgree("yes")}
                        />
                        <RadioRow
                          id="pol-no"
                          name="policiesAgree"
                          label="No"
                          checked={policiesAgree === "no"}
                          onChange={() => setPoliciesAgree("no")}
                        />
                      </div>

                      {submitAttempted &&
                        (!conflictsAware || (conflictsAware === "yes" && !conflictsExplain.trim()) || policiesAgree !== "yes") && (
                          <div className="nonprofit-signup__error">
                            Please complete conflicts & compliance.
                          </div>
                        )}
                    </section>

                    {/* 10. Final Acknowledgments */}
                    <section className="nonprofit-signup__section nonprofit-signup__section--last">
                      <div className="nonprofit-signup__section-top">
                        <div className="nonprofit-signup__step">
                          <span className="nonprofit-signup__step-number">10</span>
                        </div>
                        <div>
                          <div className="nonprofit-signup__kicker">Final Acknowledgments</div>
                        </div>
                      </div>

                      <div className="nonprofit-signup__label">
                        I understand that submitting this form does not guarantee placement
                        and does not obligate me to accept a board role. (Y/N){" "}
                        <span className="nonprofit-signup__req">*</span>
                      </div>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="ack-yes"
                          name="ackNoGuarantee"
                          label="Yes"
                          checked={ackNoGuarantee === "yes"}
                          onChange={() => setAckNoGuarantee("yes")}
                        />
                        <RadioRow
                          id="ack-no"
                          name="ackNoGuarantee"
                          label="No"
                          checked={ackNoGuarantee === "no"}
                          onChange={() => setAckNoGuarantee("no")}
                        />
                      </div>

                      <div className="nonprofit-signup__label" style={{ marginTop: "1rem" }}>
                        Permission to share my profile with vetted non-profits on the portal.
                        (Y/N) <span className="nonprofit-signup__req">*</span>
                      </div>
                      <p className="nonprofit-signup__hint" style={{ marginBottom: "0.4rem" }}>
                        If you select no, your answers will only be shared with the Apollo Citizenship team.
                      </p>
                      <div className="nonprofit-signup__choices-stack">
                        <RadioRow
                          id="perm-yes"
                          name="permissionShare"
                          label="Yes"
                          checked={permissionShare === "yes"}
                          onChange={() => setPermissionShare("yes")}
                        />
                        <RadioRow
                          id="perm-no"
                          name="permissionShare"
                          label="No"
                          checked={permissionShare === "no"}
                          onChange={() => setPermissionShare("no")}
                        />
                      </div>

                      {submitAttempted && (ackNoGuarantee !== "yes" || !permissionShare.length) ? (
                        <div className="nonprofit-signup__error">
                          Please confirm the no-guarantee acknowledgment and select a share preference.
                        </div>
                      ) : null}
                    </section>
                  </>
                )}

                <div className="nonprofit-signup__actions">
                  {!disclosureAccepted ? (
                    <div className="nonprofit-signup__unlock-notice" role="status">
                      <FontAwesomeIcon
                        icon={faLock}
                        className="nonprofit-signup__unlock-icon"
                        aria-hidden="true"
                      />
                      <p className="nonprofit-signup__unlock-text">
                        Confirm the disclosure above to continue filling out the form.
                      </p>
                    </div>
                  ) : (
                    <>
                      <button
                        type="submit"
                        className="nonprofit-signup__submit"
                        disabled={isSubmitting || (!formValid && submitAttempted)}
                      >
                        {isSubmitting ? "Submitting..." : "Submit application"}
                      </button>

                      {submitAttempted && !formValid ? (
                        <div className="nonprofit-signup__error nonprofit-signup__error--center">
                          Please complete all required fields above.
                        </div>
                      ) : (
                        <div className="nonprofit-signup__error-spacer" />
                      )}
                    </>
                  )}
                </div>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  );
}
